import React, { memo, useState, useEffect } from "react";
import {
  Table as MTable,
  TableContainer,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
  TablePagination
} from "@mui/material";
import { makeStyles, withStyles } from "@mui/styles";
import TextField from "./TextField";
import { styled } from "@mui/material/styles";
import Box from "./Box";
import TableLoadingSkeleton from "./TableLoadingSkeleton";

const StyledTableCell = withStyles({
  root: {
    borderBottom: "none !important",
    padding: "0px 0px !important"
  }
})(TableCell);

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover
  }
}));

const useStyles = makeStyles((theme) => ({
  root: {},
  headRow: {
    background: "#EDF7FF",
    borderRadius: "50%"
  },
  headTableCell: {
    color: "#707070",
    fontFamily: theme.typography.fontFamily,
    fontSize: "13px",
    fontWeight: "bold",
    padding: "20px",
    border: "1px solid #E0E0E0",
    whiteSpace: "nowrap",
    textAlign: "left"
  },
  bodyTableCell: {
    color: "#707070",
    fontFamily: theme.typography.fontFamily,
    fontSize: "13px"
  }
}));

const TableCellContent = memo(({ col, row, rowIndex }) => {
  const classes = useStyles();
  let value =
    col.field && (row[col.field] || row[col.field] === 0) ? row[col.field] : "";
  if (col.valueGetter && !col.enableEdit) {
    value = col.valueGetter(value, row);
  }
  return (
    <TableCell sx={{ verticalAlign: "initial", border: "1px solid #E0E0E0", paddingLeft: "1.275rem" }}>
      {col.enableEdit && col.onChange ? (
        <div style={col.style}>
          <TextField
            defaultValue={value}
            onChange={(e) => col.onChange(e.target.value, rowIndex)}
          />
        </div>
      ) : (
        <div className={classes.bodyTableCell} style={col.style}>
          {value || "-"}
        </div>
      )}
    </TableCell>
  );
});

const Table = ({ columns = [], data = { 0: [] }, count = 0, pagination = true, localPagination = false, page: pageProps = 0, rowsPerPage: rowsPerPageProps = 10, onPageChange = () => null, sx = {}, isLoading }) => {
  
  
  const classes = useStyles();
  const [tableState, setTableState] = useState({
    page: pageProps,
    rowsPerPage: rowsPerPageProps
  });

  let rows = data[tableState.page] || [];
  if (data.constructor === Array) {
    rows = data;
    count = count || data.length || 0;
  }

  useEffect(() => {
    // Reset pagination when data changes
    if (localPagination) {
    setTableState({ page: 0, rowsPerPage: rowsPerPageProps });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (event, newPage) => {
   
    setTableState((prevState) => ({
      ...prevState,
      page: newPage
    }));
    onPageChange(newPage + 1, tableState.rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value);
    setTableState((prevState) => ({
      ...prevState,
      page: 0,
      rowsPerPage: newRowsPerPage
    }));
    onPageChange(1, newRowsPerPage);
  };

  if (localPagination) {
    rows = rows.slice(
      tableState.page * tableState.rowsPerPage,
      tableState.page * tableState.rowsPerPage + tableState.rowsPerPage
    );
  }
  

  return (
    <>
      <TableContainer sx={sx}>
        <MTable>
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <StyledTableCell key={index}>
                  <div className={classes.headTableCell}>
                    {col.headerName}
                  </div>
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableLoadingSkeleton rows={columns} />
            ) : (
              <>
                {rows.map((row, rowIndex) => (
                  <StyledTableRow key={rowIndex}>
                    {columns.map((col, colIndex) => (
                      <TableCellContent
                        key={colIndex}
                        col={col}
                        row={row}
                        rowIndex={rowIndex}
                      />
                    ))}
                  </StyledTableRow>
                ))}
              </>
            )}
          </TableBody>
        </MTable>
      </TableContainer>
      {data.length === 0 ? (
        <Box sx={{ textAlign: "center" }}>
          <p>Data Not Found </p>
        </Box>
      ) : null}

      {pagination ? (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50 , 100]}
          component="div"
          count={count}
          rowsPerPage={tableState.rowsPerPage}
          page={parseInt(tableState.page)}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      ) : null}
    </>
  );
};

export default memo(Table);
